import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiDownload,
    FiFileText,
    FiArrowUp,
    FiArrowDown,
    FiSearch,
} from 'react-icons/fi';
import { useProfiles } from '../../hooks/useProfiles';
import {
    PageHeader,
    MetricCard,
    EmptyState,
    SkeletonTableRow,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import { telLink } from '../../utils/format';
import { APP_NAME } from '../../config';
import { buildPdfReport } from '../../utils/pdfReport';
import { formatDateDisplay } from '../../utils/dates';
import './ChildRegistrationReport.css';

const COLUMNS = [
    { key: 'childNo', label: 'Reg: No', sortable: true, width: '110px' },
    { key: 'name', label: 'Child Name', sortable: true },
    { key: 'gender', label: 'Gender', sortable: true, width: '90px' },
    { key: 'dob', label: 'DOB', sortable: true, width: '110px' },
    { key: 'registeredDate', label: 'Registered', sortable: true, width: '120px' },
    { key: 'mothersName', label: "Mother's Name", sortable: true },
    { key: 'contactNumber', label: 'Contact', sortable: false, width: '130px' },
    { key: 'gnDevision', label: 'GN Devision', sortable: true, width: '130px' },
];

const MONTHS = [
    ['01', 'Jan'],
    ['02', 'Feb'],
    ['03', 'Mar'],
    ['04', 'Apr'],
    ['05', 'May'],
    ['06', 'Jun'],
    ['07', 'Jul'],
    ['08', 'Aug'],
    ['09', 'Sep'],
    ['10', 'Oct'],
    ['11', 'Nov'],
    ['12', 'Dec'],
];

const ChildRegistrationReport = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { data: apiData, loading, error } = useProfiles();
    const childrenData = useMemo(() => apiData || [], [apiData]);

    // Multi-select: empty array means "All". Rows show a radio-style
    // indicator that fills in when the value is selected.
    const [filterMonth, setFilterMonth] = useState([]);
    const [filterYear, setFilterYear] = useState([]);
    const [filterGNDivision, setFilterGNDivision] = useState('');
    const [sort, setSort] = useState({ key: 'childNo', dir: 'asc' });
    const [openMonth, setOpenMonth] = useState(false);
    const [openYear, setOpenYear] = useState(false);
    const monthRef = useRef(null);
    const yearRef = useRef(null);

    // Close a popover when clicking outside its pill container.
    useEffect(() => {
        if (!openMonth && !openYear) return undefined;
        const handler = (e) => {
            if (openMonth && monthRef.current && !monthRef.current.contains(e.target)) {
                setOpenMonth(false);
            }
            if (openYear && yearRef.current && !yearRef.current.contains(e.target)) {
                setOpenYear(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [openMonth, openYear]);

    const toggleIn = (value, current, setter) => {
        setter(current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
    };

    const availableYears = useMemo(() => {
        const years = new Set();
        childrenData.forEach((child) => {
            if (child.registeredDate) years.add(child.registeredDate.substring(0, 4));
        });
        return Array.from(years).sort().reverse();
    }, [childrenData]);

    const filteredChildren = useMemo(() => {
        return childrenData.filter((child) => {
            let matchMonth = true;
            let matchYear = true;
            if (filterMonth.length > 0 || filterYear.length > 0) {
                if (!child.registeredDate) {
                    matchMonth = false;
                    matchYear = false;
                } else {
                    const y = child.registeredDate.substring(0, 4);
                    const m = child.registeredDate.substring(5, 7);
                    matchMonth = filterMonth.length === 0 || filterMonth.includes(m);
                    matchYear = filterYear.length === 0 || filterYear.includes(y);
                }
            }
            const matchGN =
                !filterGNDivision ||
                (child.gnDevision &&
                    child.gnDevision.toLowerCase().includes(filterGNDivision.toLowerCase()));
            return matchMonth && matchYear && matchGN;
        });
    }, [childrenData, filterMonth, filterYear, filterGNDivision]);

    const sortedChildren = useMemo(() => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        const get = (c, key) => {
            switch (key) {
                case 'gender': return c.gender || '';
                default: return c[key] || '';
            }
        };
        return [...filteredChildren].sort((a, b) => {
            const av = get(a, sort.key);
            const bv = get(b, sort.key);
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
    }, [filteredChildren, sort]);

    const toggleSort = (key) => {
        setSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
        );
    };

    const generatePDF = () => {
        const filters = [];
        if (filterMonth.length > 0) {
            filters.push(`Registration Month: ${filterMonth.map((m) => MONTHS.find(([v]) => v === m)?.[1] || m).join(', ')}`);
        }
        if (filterYear.length > 0) filters.push(`Registration Year: ${filterYear.join(', ')}`);
        if (filterGNDivision.trim()) filters.push(`GN Division: ${filterGNDivision.trim()}`);

        const stamp = new Date().toISOString().split('T')[0];
        buildPdfReport({
            title: 'Child Registration Master List',
            subtitle: `${APP_NAME} · Total Registered: ${sortedChildren.length}`,
            filters,
            sections: [
                {
                    heading: 'Registered Children',
                    head: [
                        'Reg: No',
                        'Child Name',
                        'Gender',
                        'DOB',
                        'Registered Date',
                        "Mother's Name",
                        'Contact Number',
                        'GN Devision',
                    ],
                    body: sortedChildren.map((child) => [
                        child.childNo || '-',
                        child.name || '-',
                        child.gender
                            ? child.gender.charAt(0).toUpperCase() + child.gender.slice(1)
                            : '-',
                        formatDateDisplay(child.dob),
                        formatDateDisplay(child.registeredDate),
                        child.mothersName || '-',
                        child.contactNumber || '-',
                        child.gnDevision || '-',
                    ]),
                    accent: [79, 70, 229],
                },
            ],
            fileName: `Child_Registration_Report_${stamp}.pdf`,
            toast,
        });
    };

    if (loading) {
        return (
            <div className="cr-wrapper">
                <PageHeader title="Child Registration Report" subtitle="Loading…" icon={FiFileText} />
                <div className="cr-card">
                    <table className="cr-table">
                        <thead>
                            <tr>{COLUMNS.map((c) => (<th key={c.key}>{c.label}</th>))}</tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <SkeletonTableRow key={i} cols={COLUMNS.length} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="cr-wrapper">
                <EmptyState
                    illustration="error"
                    title="Couldn't load report"
                    description={error}
                    action={
                        <PageButton type="button" variant="primary" onClick={() => window.location.reload()}>
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="cr-wrapper">
            <PageHeader
                title="Child Registration Report"
                subtitle="Master list of all registered children"
                icon={FiFileText}
                actions={
                    <>
                        <PageButton type="button" variant="ghost" startIcon={<FiArrowLeft size={16} />} onClick={() => navigate('/reports')}>
                            Back
                        </PageButton>
                        <PageButton
                            type="button"
                            variant="primary"
                            startIcon={<FiDownload size={16} />}
                            onClick={generatePDF}
                            disabled={sortedChildren.length === 0}
                        >
                            Export PDF
                        </PageButton>
                    </>
                }
            />

            <div className="cr-body">
                <div className="cr-summary">
                    <MetricCard
                        label="Showing"
                        value={sortedChildren.length}
                        accent="primary"
                        helperText={`of ${childrenData.length} total`}
                    />
                    <div className="cr-filters">
                        <div className="cr-filter">
                            <FiSearch size={14} />
                            <input
                                type="text"
                                placeholder="GN Devision…"
                                value={filterGNDivision}
                                onChange={(e) => setFilterGNDivision(e.target.value)}
                                aria-label="Filter by GN Devision"
                            />
                        </div>
                        <div className="cr-filter cr-filter--pop" ref={yearRef}>
                            <label>Year</label>
                            <button
                                type="button"
                                className="cr-pop-trigger"
                                onClick={() => { setOpenYear((o) => !o); setOpenMonth(false); }}
                                aria-haspopup="listbox"
                                aria-expanded={openYear}
                            >
                                {filterYear.length === 0
                                    ? 'All'
                                    : filterYear.length === 1
                                        ? filterYear[0]
                                        : `${filterYear.length} selected`}
                            </button>
                            {openYear && (
                                <div className="cr-popover" role="listbox">
                                    <button
                                        type="button"
                                        className={`cr-popover-row${filterYear.length === 0 ? ' is-selected' : ''}`}
                                        onClick={() => { setFilterYear([]); setOpenYear(false); }}
                                        role="option"
                                        aria-selected={filterYear.length === 0}
                                    >
                                        <span className="cr-radio" aria-hidden="true" />
                                        <span>All</span>
                                    </button>
                                    {availableYears.map((y) => (
                                        <button
                                            key={y}
                                            type="button"
                                            className={`cr-popover-row${filterYear.includes(y) ? ' is-selected' : ''}`}
                                            onClick={() => toggleIn(y, filterYear, setFilterYear)}
                                            role="option"
                                            aria-selected={filterYear.includes(y)}
                                        >
                                            <span className="cr-radio" aria-hidden="true" />
                                            <span>{y}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="cr-filter cr-filter--pop" ref={monthRef}>
                            <label>Month</label>
                            <button
                                type="button"
                                className="cr-pop-trigger"
                                onClick={() => { setOpenMonth((o) => !o); setOpenYear(false); }}
                                aria-haspopup="listbox"
                                aria-expanded={openMonth}
                            >
                                {filterMonth.length === 0
                                    ? 'All'
                                    : filterMonth.length === 1
                                        ? MONTHS.find(([v]) => v === filterMonth[0])?.[1] || filterMonth[0]
                                        : `${filterMonth.length} selected`}
                            </button>
                            {openMonth && (
                                <div className="cr-popover" role="listbox">
                                    <button
                                        type="button"
                                        className={`cr-popover-row${filterMonth.length === 0 ? ' is-selected' : ''}`}
                                        onClick={() => { setFilterMonth([]); setOpenMonth(false); }}
                                        role="option"
                                        aria-selected={filterMonth.length === 0}
                                    >
                                        <span className="cr-radio" aria-hidden="true" />
                                        <span>All</span>
                                    </button>
                                    {MONTHS.map(([v, label]) => (
                                        <button
                                            key={v}
                                            type="button"
                                            className={`cr-popover-row${filterMonth.includes(v) ? ' is-selected' : ''}`}
                                            onClick={() => toggleIn(v, filterMonth, setFilterMonth)}
                                            role="option"
                                            aria-selected={filterMonth.includes(v)}
                                        >
                                            <span className="cr-radio" aria-hidden="true" />
                                            <span>{label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="cr-card">
                    <div className="cr-table-wrap">
                        <table className="cr-table">
                            <thead>
                                <tr>
                                    {COLUMNS.map((col) => {
                                        const active = sort.key === col.key;
                                        return (
                                            <th
                                                key={col.key}
                                                style={{ width: col.width }}
                                                aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                            >
                                                {col.sortable ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleSort(col.key)}
                                                        className="cr-th-btn"
                                                    >
                                                        <span>{col.label}</span>
                                                        {active ? (sort.dir === 'asc' ? <FiArrowUp size={11} /> : <FiArrowDown size={11} />) : null}
                                                    </button>
                                                ) : (
                                                    col.label
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedChildren.map((child) => (
                                    <tr key={child.id}>
                                        <td className="cr-childno">{child.childNo || '-'}</td>
                                        <td className="fw-bold">{child.name || '-'}</td>
                                        <td>
                                            <span className={`cr-gender cr-gender--${child.gender?.toLowerCase() === 'female' ? 'female' : 'male'}`}>
                                                {child.gender ? child.gender.toUpperCase() : '-'}
                                            </span>
                                        </td>
                                        <td>{formatDateDisplay(child.dob)}</td>
                                        <td>{formatDateDisplay(child.registeredDate)}</td>
                                        <td>{child.mothersName || '-'}</td>
                                        <td>
                                            {child.contactNumber ? (
                                                <a href={telLink(child.contactNumber)} className="cr-contact">
                                                    {child.contactNumber}
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td>{child.gnDevision || '-'}</td>
                                    </tr>
                                ))}
                                {sortedChildren.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="cr-empty-cell">
                                            <EmptyState
                                                illustration={filterGNDivision ? 'search' : 'filter'}
                                                compact
                                                title={
                                                    filterGNDivision
                                                        ? 'No children match this search'
                                                        : 'No registrations in this range'
                                                }
                                            />
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChildRegistrationReport;
